import React, { useEffect, useState } from 'react';
import { useI18n } from '../context/I18nContext';

// `beforeinstallprompt` fires very early — possibly BEFORE React mounts —
// so we read it from window where index.js stashed it as the page loaded.
// Falls back to a per-mount listener for late firings.
let deferredPrompt = (typeof window !== 'undefined' && window.__icDeferredInstallPrompt) || null;

const InstallBanner = () => {
  const { t } = useI18n();
  const [visible, setVisible] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [pulse, setPulse] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  // Feb 2026 — auto-hide when any Radix Dialog is open so the banner
  // never overlaps modals like the Lucky Draw Super Final. Radix Dialog
  // exposes `data-state="open"` on its content and the body has
  // `data-scroll-locked` while a modal is active.
  const [dialogOpen, setDialogOpen] = useState(false);
  useEffect(() => {
    const check = () => {
      const anyOpen = document.body.hasAttribute('data-scroll-locked') ||
        !!document.querySelector('[role="dialog"][data-state="open"]');
      setDialogOpen(anyOpen);
    };
    check();
    const observer = new MutationObserver(check);
    observer.observe(document.body, { attributes: true, childList: true, subtree: true, attributeFilter: ['data-state', 'data-scroll-locked'] });
    return () => observer.disconnect();
  }, []);

  // Detect browser family for manual install instructions (fallback when
  // the browser hasn't fired beforeinstallprompt — common in Firefox, Brave,
  // Samsung Internet, in-app webviews, or when user-engagement heuristics
  // haven't been met yet).
  const getBrowserHelp = () => {
    const ua = window.navigator.userAgent.toLowerCase();
    if (/firefox/i.test(ua)) {
      return 'Firefox: open menu (⋮) → "Install" or "Add to Home Screen".';
    }
    if (/samsungbrowser/i.test(ua)) {
      return 'Samsung Internet: menu → "Add page to" → "Home screen".';
    }
    if (/edg\//i.test(ua)) {
      return 'Edge: address bar → install icon, or menu (⋯) → Apps → "Install this site as an app".';
    }
    if (/opr\/|opera/i.test(ua)) {
      return 'Opera: menu → "Install" / "Add to Home screen".';
    }
    return 'Chrome / Brave: address bar → install icon (⊕), or menu (⋮) → "Install app" / "Add to Home screen".';
  };

  useEffect(() => {
    const isStandalone =
      window.matchMedia('(display-mode: standalone)').matches ||
      window.navigator.standalone === true;

    const installedFlag = localStorage.getItem('ic_app_installed');
    if (isStandalone || installedFlag === 'true') return;

    const ios = /iphone|ipad|ipod/i.test(window.navigator.userAgent);
    setIsIOS(ios);

    const remindLaterTime = localStorage.getItem('ic_install_remind_later');
    if (remindLaterTime && Date.now() - Number(remindLaterTime) < 24 * 60 * 60 * 1000) return;

    setVisible(true);
    const pulseTimer = setTimeout(() => setPulse(true), 3000);

    // Refresh from window in case the early-capture stashed it before mount
    if (window.__icDeferredInstallPrompt) deferredPrompt = window.__icDeferredInstallPrompt;

    const promptReadyHandler = () => {
      deferredPrompt = window.__icDeferredInstallPrompt || deferredPrompt;
    };
    const beforeInstallHandler = (e) => {
      e.preventDefault();
      deferredPrompt = e;
      window.__icDeferredInstallPrompt = e;
    };
    const installedHandler = () => {
      localStorage.setItem('ic_app_installed', 'true');
      setVisible(false);
      // Fire-and-forget analytics ping (no UI dependency on result)
      try {
        const token = localStorage.getItem('token');
        if (token) {
          fetch(`${process.env.REACT_APP_BACKEND_URL}/api/analytics/pwa-installed`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}` },
          }).catch(() => {});
        }
      } catch (_) {}
    };

    window.addEventListener('ic-install-prompt-ready', promptReadyHandler);
    window.addEventListener('beforeinstallprompt', beforeInstallHandler);
    window.addEventListener('appinstalled', installedHandler);
    window.addEventListener('ic-installed', installedHandler);

    return () => {
      clearTimeout(pulseTimer);
      window.removeEventListener('ic-install-prompt-ready', promptReadyHandler);
      window.removeEventListener('beforeinstallprompt', beforeInstallHandler);
      window.removeEventListener('appinstalled', installedHandler);
      window.removeEventListener('ic-installed', installedHandler);
    };
  }, []);

  const installApp = async () => {
    // Re-read from window in case the event arrived after mount
    if (!deferredPrompt && window.__icDeferredInstallPrompt) {
      deferredPrompt = window.__icDeferredInstallPrompt;
    }
    // Native install path (Chrome/Edge/Brave after beforeinstallprompt fired)
    if (deferredPrompt) {
      try {
        await deferredPrompt.prompt();
        const { outcome } = await deferredPrompt.userChoice;
        if (outcome === 'accepted') {
          localStorage.setItem('ic_app_installed', 'true');
          setVisible(false);
          try {
            const token = localStorage.getItem('token');
            if (token) {
              fetch(`${process.env.REACT_APP_BACKEND_URL}/api/analytics/pwa-installed`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
              }).catch(() => {});
            }
          } catch (_) {}
        }
      } catch (_) {
        // prompt() can throw if already used; fall through to manual help
        setShowHelp(true);
      }
      deferredPrompt = null;
      window.__icDeferredInstallPrompt = null;
      return;
    }
    // Fallback: show manual instructions for this browser
    setShowHelp(true);
  };

  const remindLater = () => {
    localStorage.setItem('ic_install_remind_later', String(Date.now()));
    setVisible(false);
  };

  if (!visible || dialogOpen) return null;

  return (
    <div style={styles.banner} data-testid="install-banner">
      <style>{`@keyframes ic-install-pulse { 0%,100% { box-shadow: 0 0 0 0 rgba(76,175,80,0.55); } 50% { box-shadow: 0 0 0 10px rgba(76,175,80,0); } }`}</style>
      <div style={styles.text}>
        📲 {t('installTitle')}
        {showHelp && (
          <div style={styles.helpText} data-testid="install-banner-help">
            {getBrowserHelp()}
          </div>
        )}
      </div>

      {isIOS ? (
        <div style={styles.iosText} data-testid="install-banner-ios-text">
          {t('iosInstallHint')}
        </div>
      ) : (
        <button
          style={{ ...styles.installBtn, animation: pulse ? 'ic-install-pulse 1.6s ease-out infinite' : 'none' }}
          onClick={installApp}
          data-testid="install-banner-install-btn"
        >
          {t('installBtn')}
        </button>
      )}

      <button style={styles.laterBtn} onClick={remindLater} data-testid="install-banner-later-btn">
        {t('installLater')}
      </button>
    </div>
  );
};

const styles = {
  banner: {
    position: 'fixed',
    bottom: 15,
    left: 15,
    right: 15,
    zIndex: 9999,
    background: '#111',
    color: '#fff',
    padding: '14px',
    borderRadius: '14px',
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    alignItems: 'center',
    boxShadow: '0 5px 20px rgba(0,0,0,0.3)',
    maxWidth: 720,
    margin: '0 auto',
  },
  text: { fontSize: '14px', flex: 1, minWidth: 200 },
  helpText: {
    fontSize: '12px',
    color: '#cfd8dc',
    marginTop: 6,
    lineHeight: 1.4,
  },
  iosText: { fontSize: '13px', color: '#ccc', marginLeft: 10 },
  installBtn: {
    background: '#4CAF50',
    color: '#fff',
    border: 'none',
    padding: '8px 14px',
    borderRadius: '8px',
    marginLeft: '10px',
    cursor: 'pointer',
    fontWeight: 600,
  },
  laterBtn: {
    background: 'transparent',
    color: '#aaa',
    border: 'none',
    marginLeft: '8px',
    cursor: 'pointer',
    padding: '8px 10px',
  },
};

export default InstallBanner;
